import { useCallback } from 'react';
import { clsx } from 'clsx';

import { TOption, TOptionValue } from '@treezor/design-system';

interface ITabGroupProps {
  options: TOption[];
  selected: TOptionValue[];
  onChange: (options: TOptionValue[]) => void;
  className?: string;
  hasMultiple?: false;
  isDisabled?: boolean;
}

const getClassNames = (index: number, isLastIndex: boolean, length: number) => {
  if (length === 1) return 'cursor-auto';
  if (index === 0) return 'rounded-r-0';
  if (index > 0 && !isLastIndex) return 'rounded-r-0 rounded-l-0';
  if (isLastIndex) return 'rounded-l-0';
  return 'rounded-0';
};

export function ButtonFilter({
  options,
  selected,
  hasMultiple = false,
  onChange,
  isDisabled,
  className,
}: ITabGroupProps) {
  const handleClick = useCallback(
    (selectOption: TOption) => {
      if (!hasMultiple) {
        onChange([selectOption.value]);
        return;
      }

      const newSelectedTabs = selected.some((value) => value === selectOption.value)
        ? selected.filter((value) => value !== selectOption.value)
        : [...selected, selectOption.value];

      onChange?.(newSelectedTabs);
    },
    [onChange, hasMultiple, selected],
  );

  return (
    <div className={clsx('inline-flex flex-nowrap', !isDisabled && 'cursor-pointer', className)}>
      {options.map((option, index) => {
        const isOptionSelected = selected.find((value) => option.value === value);
        return (
          <div
            key={option.value}
            onClick={() => options?.length > 1 && handleClick(option)}
            className={clsx(
              'border-primary border-2 rounded-100 px-[10px] py-[5px] typo-body-semibold-paragraph hover:border-interactive hover:text-interactive hover:bg-secondary',
              isOptionSelected ? 'text-interactive bg-[#edf4fd]' : 'text-secondary bg-primary',
              getClassNames(index, index === options.length - 1, options.length),
              index > 0 && 'border-l-0 hover:border-l-2 hover:ml-[-2px]',
            )}
            data-testid={`tab-beneficiary-${option.value}`}
          >
            {option.label}
          </div>
        );
      })}
    </div>
  );
}
